import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useSession } from "./hooks/useSession";
import { Layout } from "./components/Layout";
import { LoginPage } from "./pages/LoginPage";
import { DashboardPage } from "./pages/DashboardPage";
import { StatsPage } from "./pages/StatsPage";
import { ChatPage } from "./pages/ChatPage";
import { AuditPage } from "./pages/AuditPage";
import { SettingsPage } from "./pages/SettingsPage";
import { CalendarPage } from "./pages/CalendarPage";
import { QuickThoughtFAB } from "./components/QuickThoughtFAB";
import { TerminalLine } from "./components/terminal/TerminalLine";
import { useEffect } from "react";

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { session, loading } = useSession();
  if (loading) return <TerminalLine dim>$ loading...</TerminalLine>;
  if (!session) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RootRedirect() {
  const { session, loading } = useSession();
  useEffect(() => { /* noop */ }, []);
  if (loading) return <TerminalLine dim>$ ...</TerminalLine>;
  return <Navigate to={session ? "/" : "/login"} replace />;
}

export default function App() {
  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<RequireAuth><Layout /></RequireAuth>}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/stats" element={<StatsPage />} />
            <Route path="/chat" element={<ChatPage />} />
            <Route path="/audit" element={<AuditPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={<RootRedirect />} />
          </Route>
        </Routes>
        <AuthAwareFAB />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function AuthAwareFAB() {
  const { session } = useSession();
  if (!session) return null;
  // FAB only on the dashboard route group, but to keep things simple we mount unconditionally.
  // The CSS positions it; the FAB will overlay on every page. In Phase 2 polish we'll filter by route.
  return <QuickThoughtFAB />;
}
